import { Controller } from "@hotwired/stimulus"

// X投稿フォームの「実際のツイート風」リアルタイムプレビュー
// 本文とメディア(画像/動画)を右カラムのプレビューに反映する
// X仕様: 画像は最大maxImages枚 / 動画は1本のみ / 画像と動画の混在不可
export default class extends Controller {
  static targets = [
    "body", "input", "bodyOutput", "mediaOutput", "error",
    "existingItem", "removeCheckbox", "newContainer",
  ]
  static values = {
    maxImages: { type: Number, default: 4 },
    placeholder: { type: String, default: "" },
    existingMedia: { type: Array, default: [] },
  }

  connect() {
    // UI上だけ削除した既存メディアの index 集合(保存するまで実DB削除しない)
    this.removedIndexes = new Set()
    this.updateBody()
    this.renderMedia()
  }

  updateBody() {
    if (!this.hasBodyOutputTarget) return
    const text = this.hasBodyTarget ? this.bodyTarget.value : ""

    if (text.trim().length === 0) {
      this.bodyOutputTarget.textContent = this.placeholderValue
      this.bodyOutputTarget.classList.add("text-base-content/50")
    } else {
      this.bodyOutputTarget.textContent = text
      this.bodyOutputTarget.classList.remove("text-base-content/50")
    }
  }

  // 既存メディアを UI 上で削除(実DB削除は保存時)
  removeExisting(event) {
    const index = Number(event.currentTarget.dataset.mediaIndex)
    this.removedIndexes.add(index)

    // 対応するサムネを非表示
    const item = this.existingItemTargets.find((el) => Number(el.dataset.mediaIndex) === index)
    if (item) item.classList.add("hidden")

    // remove_media_ids[] のチェックボックスを on(保存時に実削除)
    const checkbox = this.removeCheckboxTargets.find((el) => Number(el.dataset.mediaIndex) === index)
    if (checkbox) checkbox.checked = true

    this.renderMedia()
  }

  // ファイル選択時のバリデーション + プレビュー更新
  updateMedia() {
    this.clearError()
    const files = this.newFiles()
    const remaining = this.remainingExistingMedia()

    const images = files.filter((f) => f.type.startsWith("image/")).length +
                   remaining.filter((m) => m.type === "image").length
    const videos = files.filter((f) => f.type.startsWith("video/")).length +
                   remaining.filter((m) => m.type === "video").length

    const error = this.validate(images, videos)
    if (error) {
      this.showError(error)
      if (this.hasInputTarget) this.inputTarget.value = ""
    }
    this.renderNewThumbs()
    this.renderMedia()
  }

  // 左カラムに新規追加ファイルのサムネを描画(個別削除ボタン付き)
  renderNewThumbs() {
    if (!this.hasNewContainerTarget) return
    const container = this.newContainerTarget
    const files = this.newFiles()
    container.innerHTML = ""

    if (files.length === 0) {
      container.classList.add("hidden")
      return
    }
    container.classList.remove("hidden")

    files.forEach((file, index) => {
      const isVideo = file.type.startsWith("video/")
      const url = URL.createObjectURL(file)

      const wrapper = document.createElement("div")
      wrapper.className = "relative group"

      const box = document.createElement("div")
      box.className = "rounded-lg overflow-hidden border-2 border-base-300 aspect-square bg-base-200"
      if (isVideo) {
        const video = document.createElement("video")
        video.src = url
        video.className = "w-full h-full object-cover"
        video.muted = true
        video.preload = "metadata"
        box.appendChild(video)
      } else {
        const img = document.createElement("img")
        img.src = url
        img.className = "w-full h-full object-cover"
        img.onload = () => URL.revokeObjectURL(url)
        box.appendChild(img)
      }
      wrapper.appendChild(box)

      const btn = document.createElement("button")
      btn.type = "button"
      btn.title = "削除"
      btn.className = "absolute top-1 right-1 btn btn-circle btn-xs btn-error"
      btn.innerHTML = '<span class="iconify lucide--x size-3.5"></span>'
      btn.addEventListener("click", () => this.removeNewFile(index))
      wrapper.appendChild(btn)

      container.appendChild(wrapper)
    })
  }

  // 新規追加ファイルから index 番目を取り除く(input.files を再構築)
  removeNewFile(index) {
    const files = this.newFiles()
    const dt = new DataTransfer()
    files.forEach((file, i) => {
      if (i !== index) dt.items.add(file)
    })
    this.inputTarget.files = dt.files
    this.clearError()
    this.renderNewThumbs()
    this.renderMedia()
  }

  // 既存(未削除) + 新規ファイル を合成してプレビュー描画
  renderMedia() {
    const items = [
      ...this.remainingExistingMedia().map((m) => ({ type: m.type, src: m.url })),
      ...this.newFiles().map((f) => ({
        type: f.type.startsWith("video/") ? "video" : "image",
        src: URL.createObjectURL(f),
        revoke: true,
      })),
    ]
    this.renderItems(items)
  }

  remainingExistingMedia() {
    return this.existingMediaValue.filter((m) => !this.removedIndexes.has(m.index))
  }

  newFiles() {
    return this.hasInputTarget ? Array.from(this.inputTarget.files) : []
  }

  validate(imageCount, videoCount) {
    if (imageCount > 0 && videoCount > 0) {
      return "画像と動画は同時に投稿できません"
    }
    if (imageCount > this.maxImagesValue) {
      return `画像は最大${this.maxImagesValue}枚までです`
    }
    if (videoCount > 1) {
      return "動画は1本までです"
    }
    return null
  }

  // items: [{ type: "image"|"video", src: string, revoke?: boolean }]
  renderItems(items) {
    const container = this.mediaOutputTarget
    container.innerHTML = ""

    if (!items || items.length === 0) {
      container.classList.add("hidden")
      return
    }
    container.classList.remove("hidden")

    // X風グリッド: 1枚=単一, 2枚=横2, 3枚=左1+右2, 4枚=2x2
    const count = Math.min(items.length, 4)
    container.className = `mt-3 rounded-2xl overflow-hidden border border-base-300 grid gap-0.5 ${this.gridClass(count)}`

    items.slice(0, 4).forEach((item, index) => {
      const cell = document.createElement("div")
      cell.className = `relative overflow-hidden bg-base-200 ${this.cellClass(count, index)}`

      if (item.type === "video") {
        const video = document.createElement("video")
        video.src = item.src
        video.className = "w-full h-full object-cover"
        video.muted = true
        video.preload = "metadata"
        cell.appendChild(video)

        const badge = document.createElement("span")
        badge.className =
          "absolute bottom-1 left-1 iconify lucide--play text-white drop-shadow size-4"
        cell.appendChild(badge)
      } else {
        const img = document.createElement("img")
        img.src = item.src
        img.className = "w-full h-full object-cover"
        if (item.revoke) img.onload = () => URL.revokeObjectURL(item.src)
        cell.appendChild(img)
      }
      container.appendChild(cell)
    })
  }

  gridClass(count) {
    if (count === 1) return "grid-cols-1"
    return "grid-cols-2"
  }

  // 3枚レイアウトは左が縦2行ぶち抜き
  cellClass(count, index) {
    if (count === 1) return "aspect-video"
    if (count === 3 && index === 0) return "row-span-2 aspect-square"
    return "aspect-square"
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
